import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { httpOk, GET, POST, DELETE } from 'helpers/api'
import { formatDate, regexPhoneNumber, notify } from 'helpers/helpers'

// Представители организации: клиенты, которые от её имени входят в Telegram Mini App
// (долг, кэшбэк, акт сверки, заказы). Привязать можно по телефону или одноразовой ссылкой.
function Representatives({ organization }) {
	const { t } = useTranslation()

	const [rows, setRows] = useState([])
	const [phone, setPhone] = useState('')
	const [role, setRole] = useState('owner')
	const [inviteRole, setInviteRole] = useState('owner')
	const [invite, setInvite] = useState(null)
	const [confirmId, setConfirmId] = useState(null)

	async function getRows() {
		if (!organization.id) return
		const response = await GET(`/organizations-representatives?organization_id=${organization.id}`)
		if (httpOk(response)) setRows(response.data.data ?? [])
	}

	async function addByPhone() {
		const response = await POST('/organizations-representatives', {
			'organization_id': organization.id,
			'phone': phone,
			'role': role,
		}, { loader: true })

		if (httpOk(response)) {
			setPhone('')
			getRows()
		}
	}

	async function remove(row) {
		// Снятие — в два нажатия: первое только просит подтвердить
		if (confirmId !== row.client_id) {
			setConfirmId(row.client_id)
			return
		}

		const response = await DELETE(`/organizations-representatives/${organization.id}/${row.client_id}`, {}, { loader: true })
		setConfirmId(null)
		if (httpOk(response)) getRows()
	}

	async function createInvite() {
		const response = await POST('/organizations-representatives-invite', {
			'organization_id': organization.id,
			'role': inviteRole,
		}, { loader: true })

		if (httpOk(response)) setInvite(response.data)
	}

	async function copyInvite() {
		try {
			await navigator.clipboard.writeText(invite.url)
			notify(t('org_rep_copied'), 'success')
		} catch (e) {
			// Буфер недоступен (старый браузер, нет прав) — ссылка всё равно выделена в поле
			document.getElementById('org-rep-invite-url')?.select()
		}
	}

	useEffect(() => {
		setInvite(null)
		setConfirmId(null)
		getRows()
	}, [organization.id]) // eslint-disable-line react-hooks/exhaustive-deps

	const roles = [
		{ 'id': 'owner', 'name': t('org_rep_role_owner') },
		{ 'id': 'orderer', 'name': t('org_rep_role_orderer') },
	]

	return (
		<>
			<div className="table-responsive mb-3">
				<table className="table table-bordered mb-0">
					<thead>
						<tr>
							<th>{t('phone')}</th>
							<th>{t('name2')}</th>
							<th>{t('org_rep_role')}</th>
							<th className="text-center">Telegram</th>
							<th className="text-center">{t('created')}</th>
							<th style={{ 'minWidth': '33px' }}></th>
						</tr>
					</thead>
					<tbody>
						{rows.length === 0 &&
							<tr>
								<td colSpan="6" className="text-center text-muted">{t('org_rep_empty')}</td>
							</tr>
						}
						{rows.map((row) => (
							<tr key={row.id}>
								<td className="text-nowrap">{regexPhoneNumber(row.phone1)}</td>
								<td className="td-ellipsis">{row.first_name}</td>
								<td>{row.role === 'orderer' ? t('org_rep_role_orderer') : t('org_rep_role_owner')}</td>
								<td className="text-center">
									{row.in_telegram
										? <i className="uil-check text-success" title={t('org_rep_telegram_yes')}></i>
										: <span className="text-muted" title={t('org_rep_telegram_no')}>—</span>
									}
								</td>
								<td className="text-center text-nowrap">{formatDate(row.created_at)}</td>
								<td>
									<button type="button"
										className={`btn btn-sm ${confirmId === row.client_id ? 'btn-danger' : 'btn-outline-danger'}`}
										onClick={() => remove(row)}>
										{confirmId === row.client_id ? t('org_rep_remove_confirm') : <i className="uil-times"></i>}
									</button>
								</td>
							</tr>
						))}
					</tbody>
				</table>
			</div>

			<h6>{t('org_rep_add_by_phone')}</h6>
			<div className="d-flex gap-2 mb-1">
				<input type="text" className="form-control" placeholder="998 90 123 45 67"
					value={phone}
					onChange={(e) => setPhone(e.target.value.replace(/[^0-9+ ]/g, ''))} />
				<select className="form-select w-auto" value={role} onChange={(e) => setRole(e.target.value)}>
					{roles.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
				</select>
				<button type="button" className="btn btn-primary text-nowrap" disabled={!phone} onClick={addByPhone}>
					{t('add')}
				</button>
			</div>
			<small className="text-muted d-block mb-4">{t('org_rep_role_hint')}</small>

			<h6>{t('org_rep_invite')}</h6>
			<div className="d-flex gap-2 mb-1">
				<select className="form-select w-auto" value={inviteRole} onChange={(e) => setInviteRole(e.target.value)}>
					{roles.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
				</select>
				<button type="button" className="btn btn-outline-primary text-nowrap" onClick={createInvite}>
					{t('org_rep_invite_create')}
				</button>
			</div>
			{invite &&
				<div className="d-flex gap-2 mt-2">
					<input id="org-rep-invite-url" type="text" className="form-control" readOnly value={invite.url}
						onFocus={(e) => e.target.select()} />
					<button type="button" className="btn btn-primary text-nowrap" onClick={copyInvite}>
						{t('org_rep_invite_copy')}
					</button>
				</div>
			}
			<small className="text-muted d-block">{t('org_rep_invite_hint')}</small>
		</>
	)
}

export default Representatives
